import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, ArrowRight, Award, Download, FileSearch, History, Shield, ShieldCheck, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useAuditLog,
  useCandidates,
  useExpedientesList,
  useGroupsList,
  useStandards,
  useStandardsList,
  useUsers,
  useUsersByRole,
} from "@/store/hooks";
import { computeAlerts, type ComputedAlert } from "@/utils/auditAlerts";
import { downloadCSV, downloadJSON } from "@/utils/download";

export default function AuditorDashboard() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("auditor"), [setRole]);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const standards = useStandards();
  const standardsList = useStandardsList();
  const groups = useGroupsList();
  const expedientes = useExpedientesList();
  const candidates = useCandidates();
  const users = useUsers();
  const auditLog = useAuditLog();
  const evaluadores = useUsersByRole("evaluador");

  const [fEstandar, setFEstandar] = useState<string>("");
  const [fGrupo, setFGrupo] = useState<string>("");
  const [fEvaluador, setFEvaluador] = useState<string>("");
  const [fDesde, setFDesde] = useState<string>("");
  const [fHasta, setFHasta] = useState<string>("");

  // Aplicar filtros sobre expedientes
  const filteredExpedientes = useMemo(() => {
    return expedientes.filter((e) => {
      if (fEstandar && e.estandar !== fEstandar) return false;
      if (fGrupo && e.grupoId !== fGrupo) return false;
      if (fEvaluador) {
        const g = groups.find((x) => x.id === e.grupoId);
        const evs = g?.evaluadoresIds && g.evaluadoresIds.length > 0 ? g.evaluadoresIds : [g?.evaluadorId];
        if (!evs.includes(fEvaluador)) return false;
      }
      // filtro por rango de fechas — usamos la fecha más relevante (evaluación práctica o examen teórico)
      const fechaRef = e.evaluacionPractica.fecha !== "—" ? e.evaluacionPractica.fecha
        : e.examenTeorico.fecha !== "—" ? e.examenTeorico.fecha
        : e.diagnostico.fecha;
      if (fDesde && fechaRef && fechaRef < fDesde) return false;
      if (fHasta && fechaRef && fechaRef > fHasta) return false;
      return true;
    });
  }, [expedientes, groups, fEstandar, fGrupo, fEvaluador, fDesde, fHasta]);

  // KPIs reactivos
  const kpis = useMemo(() => {
    const total = filteredExpedientes.length;
    const conDictamen = filteredExpedientes.filter((e) => e.dictamen !== "pendiente").length;
    const certificados = filteredExpedientes.filter((e) => e.certificado.emitido).length;
    const competentes = filteredExpedientes.filter((e) => e.dictamen === "competente").length;
    const pctDictaminados = total > 0 ? Math.round((conDictamen / total) * 100) : 0;
    const pctCompetencia = total > 0 ? Math.round((competentes / total) * 100) : 0;
    return { total, conDictamen, certificados, pctDictaminados, pctCompetencia };
  }, [filteredExpedientes]);

  // Alertas calculadas en vivo
  const alerts = useMemo<ComputedAlert[]>(() => {
    const all = computeAlerts({
      expedientes: filteredExpedientes,
      groups,
      standards,
      candidates,
      users,
      today,
    });
    return all;
  }, [filteredExpedientes, groups, standards, candidates, users, today]);

  const alertsByTone = useMemo(() => ({
    alta: alerts.filter((a) => a.severidad === "alta").length,
    media: alerts.filter((a) => a.severidad === "media").length,
    baja: alerts.filter((a) => a.severidad === "baja").length,
  }), [alerts]);

  // Eventos recientes del auditLog (filtrados por expedientes de la vista cuando aplique)
  const recentEvents = useMemo(() => {
    const expedienteIds = new Set(filteredExpedientes.map((e) => e.id));
    return auditLog
      .filter((ev) => (ev.entidad === "expediente" ? expedienteIds.has(ev.entidadId) : true))
      .slice(0, 8);
  }, [auditLog, filteredExpedientes]);

  const clearFilters = () => {
    setFEstandar(""); setFGrupo(""); setFEvaluador(""); setFDesde(""); setFHasta("");
  };

  const handleExport = (kind: "csv" | "json") => {
    const rows = filteredExpedientes.map((e) => {
      const cand = candidates[e.candidatoId];
      const g = groups.find((x) => x.id === e.grupoId);
      return {
        expediente: e.id,
        estandar: e.estandar,
        grupo: g?.nombre ?? e.grupoId,
        candidato: cand?.nombre ?? e.candidatoId,
        curp: cand?.curp ?? "",
        diagnostico_puntaje: e.diagnostico.puntaje ?? "",
        teorico_puntaje: e.examenTeorico.puntaje ?? "",
        teorico_aprobado: e.examenTeorico.aprobado,
        practica_aplicada: e.evaluacionPractica.aplicada,
        practica_pct: e.evaluacionPractica.criteriosTotales > 0
          ? Math.round((e.evaluacionPractica.criteriosCompetente / e.evaluacionPractica.criteriosTotales) * 100)
          : 0,
        practica_fecha: e.evaluacionPractica.fecha,
        dictamen: e.dictamen,
        certificado_emitido: e.certificado.emitido,
        certificado_folio: e.certificado.folio ?? "",
        certificado_fecha: e.certificado.fechaEmision ?? "",
      };
    });
    const fname = `auditoria-expedientes-${today}`;
    if (kind === "csv") downloadCSV(`${fname}.csv`, rows);
    else downloadJSON(`${fname}.json`, { generadoEn: new Date().toISOString(), filtros: { fEstandar, fGrupo, fEvaluador, fDesde, fHasta }, expedientes: rows, alertas: alerts });
  };

  const filteredGroups = useMemo(
    () => fEstandar ? groups.filter((g) => g.estandar === fEstandar) : groups,
    [groups, fEstandar],
  );

  return (
    <AppLayout pageTitle="Auditoría del proceso">
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Integridad del proceso CertiCore</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>Vista reactiva con filtros — los KPIs y alertas se calculan en vivo</div>
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <Field label="Estándar">
            <select data-testid="filter-estandar" value={fEstandar} onChange={(e) => { setFEstandar(e.target.value); setFGrupo(""); }} style={selStyle}>
              <option value="">Todos</option>
              {standardsList.map((s) => <option key={s.codigo} value={s.codigo}>{s.codigo}</option>)}
            </select>
          </Field>
          <Field label="Grupo">
            <select data-testid="filter-grupo" value={fGrupo} onChange={(e) => setFGrupo(e.target.value)} style={selStyle}>
              <option value="">Todos</option>
              {filteredGroups.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </Field>
          <Field label="Evaluador">
            <select data-testid="filter-evaluador" value={fEvaluador} onChange={(e) => setFEvaluador(e.target.value)} style={selStyle}>
              <option value="">Todos</option>
              {evaluadores.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </Field>
          <Field label="Desde">
            <input data-testid="filter-desde" type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)} style={selStyle} />
          </Field>
          <Field label="Hasta">
            <input data-testid="filter-hasta" type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)} style={selStyle} />
          </Field>
          <PrimaryButton variant="outline" testId="button-clear-filters" onClick={clearFilters}>Limpiar</PrimaryButton>
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <PrimaryButton variant="outline" testId="button-export-csv" onClick={() => handleExport("csv")}><Download size={13} /> Exportar CSV</PrimaryButton>
          <PrimaryButton variant="outline" testId="button-export-json" onClick={() => handleExport("json")}><Download size={13} /> Exportar JSON</PrimaryButton>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 16 }}>
        <Kpi icon={FileSearch} label="Expedientes en vista" value={String(kpis.total)} tone="var(--cc-navy)" />
        <Kpi icon={ShieldCheck} label="Dictaminados" value={`${kpis.pctDictaminados}%`} tone="var(--cc-teal)" hint={`${kpis.conDictamen}/${kpis.total}`} />
        <Kpi icon={Award} label="Certificados activos" value={String(kpis.certificados)} tone="var(--cc-green)" />
        <Kpi icon={AlertTriangle} label="Alertas activas" value={String(alerts.length)} tone={alerts.length > 0 ? "var(--cc-amber)" : "var(--cc-gray-400)"} hint={`${alertsByTone.alta} altas · ${alertsByTone.media} medias`} />
        <Kpi icon={Shield} label="% competencia" value={`${kpis.pctCompetencia}%`} tone="var(--cc-teal)" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card>
          <SectionTitle action={<Link href="/auditor/bitacora" data-testid="link-bitacora" style={{ fontSize: 12, color: "var(--cc-teal)", fontWeight: 600, textDecoration: "none" }}>Ver bitácora →</Link>}>
            Alertas activas calculadas
          </SectionTitle>
          {alerts.length === 0 ? (
            <div style={{ padding: 18, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }} data-testid="text-no-alerts">
              No hay alertas para la vista filtrada actual.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="list-alerts">
              {alerts.map((a) => (
                <AlertRow key={a.id} alert={a} candidateName={a.expedienteId ? candidates[expedientes.find((e) => e.id === a.expedienteId)?.candidatoId ?? ""]?.nombre : undefined} />
              ))}
            </div>
          )}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle action={<Link href="/auditor/bitacora" style={{ fontSize: 12, color: "var(--cc-teal)", fontWeight: 600, textDecoration: "none" }}>Completa →</Link>}>
              Actividad reciente
            </SectionTitle>
            {recentEvents.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Sin eventos registrados todavía.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {recentEvents.map((ev) => (
                  <li key={ev.id} style={{ fontSize: 12, color: "var(--cc-gray-700)", borderBottom: "1px solid var(--cc-gray-100)", paddingBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span><strong>{users[ev.actor]?.nombre ?? ev.actor}</strong> {ev.accion}</span>
                      <span style={{ color: "var(--cc-gray-400)", fontFamily: "var(--app-font-mono)", fontSize: 10 }}>{ev.ts.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div style={{ color: "var(--cc-gray-500)", fontSize: 11 }}>{ev.entidad}: {ev.entidadId}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Link href="/auditor/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
            <Card style={{ cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Sparkles size={15} color="var(--cc-teal)" />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>Pregúntale a CertiAI</span>
                <ArrowRight size={13} color="var(--cc-teal)" style={{ marginLeft: "auto" }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>Inconsistencias, vigencias y resumen de bitácora en chat.</div>
            </Card>
          </Link>

          <Card>
            <SectionTitle>Expedientes en vista</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="list-expedientes">
              {filteredExpedientes.slice(0, 6).map((e) => {
                const c = candidates[e.candidatoId];
                return (
                  <Link key={e.id} href={`/auditor/expediente/${e.id}`} data-testid={`row-exp-${e.id}`} style={{ textDecoration: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 8, border: "1px solid var(--cc-gray-100)", borderRadius: 8, cursor: "pointer" }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{c?.nombre ?? e.candidatoId}</div>
                        <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{e.id.toUpperCase()} · {e.estandar}</div>
                      </div>
                      <Badge tone={e.dictamen === "competente" ? "green" : e.dictamen === "todavia-no-competente" ? "red" : "gray"}>{e.dictamen}</Badge>
                    </div>
                  </Link>
                );
              })}
              {filteredExpedientes.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Sin expedientes en la vista.</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

const selStyle: React.CSSProperties = { padding: "7px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 12, background: "white", color: "var(--cc-navy)" };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, hint }: { icon: React.ComponentType<{ size?: number; color?: string }>; label: string; value: string; tone: string; hint?: string }) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: tone, color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={17} />
        </div>
        {hint && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)" }}>{hint}</span>}
      </div>
      <div style={{ marginTop: 10, fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{label}</div>
    </Card>
  );
}

function AlertRow({ alert, candidateName }: { alert: ComputedAlert; candidateName?: string }) {
  const tone = alert.severidad === "alta" ? "red" : alert.severidad === "media" ? "amber" : "gray";
  const target = alert.expedienteId ? `/auditor/expediente/${alert.expedienteId}` : "/auditor";
  return (
    <Link href={target} data-testid={`alert-${alert.id}`} style={{ textDecoration: "none" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10, cursor: "pointer" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: alert.severidad === "alta" ? "var(--cc-red-bg)" : alert.severidad === "media" ? "var(--cc-amber-bg)" : "var(--cc-gray-100)", color: alert.severidad === "alta" ? "var(--cc-red)" : alert.severidad === "media" ? "var(--cc-amber)" : "var(--cc-gray-500)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {alert.tipo === "certificado-por-vencer" ? <History size={17} /> : <AlertTriangle size={17} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{alert.titulo}</div>
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{alert.detalle}{candidateName ? ` · ${candidateName}` : ""}</div>
        </div>
        <Badge tone={tone}>{alert.severidad}</Badge>
      </div>
    </Link>
  );
}
